/**
 * NewMessagePane — New message in the Team dock (staff chat MVP, SR-14), ported from Antonin's catalog source
 * (Signal DS `catalog/src/components/TeamChat/TeamChat.tsx`, `ChatWindow` without a conversation + `NewTo`): the header
 * "New message", "To:" with a search by name or role over GET staff-chat/directory, the matches as a list, and the
 * composer ("Write the first message…").
 *
 * One-to-one only for the MVP (the catalog's "two or more make a group" is not ported): one person can be picked, and
 * shows as a chip. Picking someone you already talk to opens that conversation. Anyone else: the first message finds or
 * creates the thread (POST threads { participantUserId, patientId? }), the dock opens it, and it sends the message there
 * with its clientMessageId. Enter in "To:" picks the first match.
 */
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Avatar, Banner } from '@scripsteam/scrips-signal-ds';
import { useCreateStaffThread, useStaffChatIdentity, useStaffDirectory, useStaffThreads } from '@/staff-chat/services/use-staff-chat';
import { openThreadError, staffChatErrorKind, NO_ACCESS_TITLE, UNAVAILABLE_TITLE } from '@/staff-chat/lib/staff-chat-errors';
import { newClientMessageId, roleLabel } from '@/staff-chat/lib/chat-format';
import { useDockStore } from '@/staff-chat/lib/dock-store';
import { useHubStatusStore } from '@/staff-chat/lib/hub-status-store';
import type { StaffChatPerson } from '@/staff-chat/types/staff-chat';
import { Composer, Reconnecting, SkeletonLines } from './team-chat-parts';

export interface NewMessagePaneProps {
  /** A patient thread is being started ("Message a colleague", patient switch only). */
  patientId?: string;
}

export function NewMessagePane({ patientId }: NewMessagePaneProps) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<StaffChatPerson | null>(null);
  const { me } = useStaffChatIdentity();
  const directory = useStaffDirectory(true);
  const threads = useStaffThreads();
  const create = useCreateStaffThread();
  const selectThread = useDockStore((s) => s.selectThread);
  const openWithFirstMessage = useDockStore((s) => s.openWithFirstMessage);
  const reconnecting = useHubStatusStore((s) => s.status === 'reconnecting');

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return (directory.data ?? []).filter((s) => s.name.toLowerCase().includes(t) || roleLabel(s).toLowerCase().includes(t));
  }, [q, directory.data]);

  const pick = (person: StaffChatPerson) => {
    // A one-to-one conversation already in the list opens as it is (a patient thread is always found or made by the server).
    const existing = patientId
      ? undefined
      : threads.data?.items.find(
          (t) => t.kind === 'direct' && t.participants.length === 2 && t.participants.some((p) => p.userId === person.userId && p.userId !== me),
        );
    if (existing) return selectThread(existing.id);
    setPicked(person);
    setQ('');
  };

  const sendFirst = (body: string) => {
    if (!picked) return;
    create.mutate(
      { participantUserId: picked.userId, ...(patientId ? { patientId } : {}) },
      { onSuccess: (thread) => openWithFirstMessage({ threadId: thread.id, body, clientMessageId: newClientMessageId() }) },
    );
  };

  const directoryError =
    directory.isError ? (
      staffChatErrorKind(directory.error) === 'no-access' ? (
        NO_ACCESS_TITLE
      ) : staffChatErrorKind(directory.error) === 'unavailable' ? (
        UNAVAILABLE_TITLE
      ) : (
        <>
          Couldn&apos;t load your colleagues.{' '}
          <button type="button" className="team-chat-link" onClick={() => void directory.refetch()}>
            Retry
          </button>
        </>
      )
    ) : null;

  return (
    <section className="team-chat-window" aria-label="New message" data-slot="team-chat-new-message">
      <header className="team-chat-head">
        <span className="team-chat-who">
          <span className="team-chat-name">New message</span>
        </span>
      </header>
      {reconnecting ? <Reconnecting /> : null}
      <div className="team-chat-to">
        <span className="team-chat-to-label">To:</span>
        {picked ? (
          <span className="team-chat-chip">
            {picked.name}
            <button type="button" aria-label={`Remove ${picked.name}`} onClick={() => setPicked(null)}>
              <X aria-hidden />
            </button>
          </span>
        ) : (
          <input
            className="team-chat-to-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && results[0]) {
                e.preventDefault();
                pick(results[0]);
              }
            }}
            placeholder="Name or role"
            aria-label="To"
            autoComplete="off"
            // The pane opens for this one field.
            autoFocus
          />
        )}
      </div>
      {directory.isPending && q.trim() ? (
        <div className="team-chat-pad">
          <SkeletonLines label="Loading colleagues" />
        </div>
      ) : directoryError ? (
        <p className="team-chat-note team-chat-pad">{directoryError}</p>
      ) : results.length > 0 ? (
        <ul className="team-chat-results" role="listbox" aria-label="Colleagues">
          {results.map((s, i) => (
            // A pointer pick; the keyboard picks the first match with Enter in "To:".
            <li key={s.userId} role="option" aria-selected={i === 0} className="team-chat-result" onClick={() => pick(s)}>
              <Avatar scale="compact" tone="provider" name={s.name} size={32} />
              <span className="team-chat-body">
                <span className="team-chat-title">{s.name}</span>
                <span className="team-chat-meta">{roleLabel(s)}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : q.trim() && !picked ? (
        <p className="team-chat-note team-chat-pad">No colleague matches “{q.trim()}”.</p>
      ) : null}
      {create.isError ? <Banner variant="error">{openThreadError(create.error)}</Banner> : null}
      <div className="team-chat-thread" />
      <Composer placeholder="Write the first message…" onSend={sendFirst} disabled={!picked || create.isPending} />
    </section>
  );
}
